/**
 * EntityAuditHistory
 *
 * Collapsible panel embedded inside detail modals (User / Company / Order)
 * that shows the last N audit events for that entity.
 *
 * Backed by GET /api/admin/audit-logs/by-entity?type=...&id=...
 */

import React, { useEffect, useState } from 'react';
import {
  FiChevronDown, FiChevronUp, FiClock, FiExternalLink,
  FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiUserCheck, FiUserX,
  FiShield, FiLock, FiUnlock, FiLogIn, FiLogOut, FiUpload, FiDownload,
  FiAlertCircle, FiActivity,
} from 'react-icons/fi';
import { auditLogsAPI } from '../../services/api';
import AuditLogDetailModal from './AuditLogDetailModal';
import { ROUTES } from '../../constants/constants';
import { useNavigate } from 'react-router-dom';

// Humanize a dot-notation action key into a sentence.
// Examples:
//   draft.visibility_changed -> "Visibility changed"
//   admin.user.role_change   -> "Role changed"
//   user.login.failed        -> "Login failed"
function humanizeAction(action) {
  if (!action) return 'Event';
  const parts = String(action).split('.');
  const verb = parts[parts.length - 1] || action;
  return verb
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

// Map an action key to an icon + accent color. Falls back to a neutral dot.
function actionVisual(action) {
  const key = String(action || '').toLowerCase();
  if (key.includes('create')) return { Icon: FiPlus, color: '#4a6b3f', bg: '#e8efe5' };
  if (key.includes('delete')) return { Icon: FiTrash2, color: '#9a564a', bg: '#f2e9e6' };
  if (key.includes('update') || key.includes('edit') || key.includes('change'))
    return { Icon: FiEdit2, color: '#3a4a66', bg: '#edeef3' };
  if (key.includes('visibility') && key.includes('public'))
    return { Icon: FiEye, color: '#145f5f', bg: '#e3f0ed' };
  if (key.includes('visibility') && key.includes('private'))
    return { Icon: FiEyeOff, color: '#6f6049', bg: '#efe9e0' };
  if (key.includes('visibility')) return { Icon: FiEye, color: '#145f5f', bg: '#e3f0ed' };
  if (key.includes('activate') && !key.includes('deactivate'))
    return { Icon: FiUserCheck, color: '#4a6b3f', bg: '#e8efe5' };
  if (key.includes('deactivate')) return { Icon: FiUserX, color: '#8a6a1f', bg: '#f3ebd7' };
  if (key.includes('role')) return { Icon: FiShield, color: '#3a4a66', bg: '#edeef3' };
  if (key.includes('lock') && !key.includes('unlock'))
    return { Icon: FiLock, color: '#8a6a1f', bg: '#f3ebd7' };
  if (key.includes('unlock')) return { Icon: FiUnlock, color: '#4a6b3f', bg: '#e8efe5' };
  if (key.includes('login')) return { Icon: FiLogIn, color: '#145f5f', bg: '#e3f0ed' };
  if (key.includes('logout') || key.includes('revoke'))
    return { Icon: FiLogOut, color: '#8a6a1f', bg: '#f3ebd7' };
  if (key.includes('upload')) return { Icon: FiUpload, color: '#3a4a66', bg: '#edeef3' };
  if (key.includes('download') || key.includes('export'))
    return { Icon: FiDownload, color: '#3a4a66', bg: '#edeef3' };
  if (key.includes('fail') || key.includes('error'))
    return { Icon: FiAlertCircle, color: '#9a564a', bg: '#f2e9e6' };
  return { Icon: FiActivity, color: '#7a7060', bg: '#f0ebe0' };
}

function fmtRelative(d) {
  try {
    const date = new Date(d);
    const diff = Date.now() - date.getTime();
    const min = Math.round(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.round(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (e) {
    return String(d);
  }
}

function fmtAbsolute(d) {
  try {
    return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return String(d);
  }
}

export default function EntityAuditHistory({ entityType, entityId, limit = 20, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !entityId) return;
    if (rows !== null) return; // already loaded
    setLoading(true);
    setError(null);
    auditLogsAPI
      .getByEntity({ type: entityType, id: entityId, limit })
      .then((res) => setRows(res?.data?.rows || []))
      .catch((err) => {
        setError(err?.message || 'Failed to load history');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [open, entityId, entityType, limit, rows]);

  if (!entityId) return null;

  return (
    <div
      className="bg-white rounded-[12px] border overflow-hidden"
      style={{ borderColor: '#e8e2d6', boxShadow: '0 1px 3px rgba(8,17,31,.04)' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-2.5 border-b flex items-center gap-2 text-left transition-colors hover:bg-[#f8f5ef]"
        style={{ borderColor: open ? '#e8e2d6' : 'transparent', background: 'linear-gradient(180deg, #fdfbf6 0%, #ffffff 100%)' }}
      >
        <span className="w-[3px] h-3.5 rounded-full shrink-0" style={{ background: '#145f5f' }} />
        <FiClock className="text-[12px] text-[#145f5f]" />
        <span className="text-[11.5px] font-bold text-[#08111f] tracking-tight flex-1">Audit History</span>
        {rows?.length > 0 && (
          <span className="text-[10px] text-[#7a7060]">{rows.length} recent event{rows.length === 1 ? '' : 's'}</span>
        )}
        {open ? <FiChevronUp /> : <FiChevronDown />}
      </button>

      {open && (
        <div className="p-4">
          {loading && <div className="text-[11.5px] italic text-[#7a7060] animate-pulse">Loading…</div>}
          {error && <div className="text-[11.5px]" style={{ color: '#9a564a' }}>{error}</div>}
          {!loading && rows && rows.length === 0 && (
            <div className="text-[11.5px] italic text-[#7a7060]">No audit events recorded for this entity yet.</div>
          )}
          {rows && rows.length > 0 && (
            <>
              {/* Timeline: a single vertical guide line with a tinted dot per event. */}
              <ol className="relative">
                <span
                  aria-hidden="true"
                  className="absolute top-1 bottom-1 w-px"
                  style={{ left: '11px', background: '#e8e2d6' }}
                />
                {rows.map((row) => {
                  const { Icon, color, bg } = actionVisual(row.action);
                  const label = humanizeAction(row.action);
                  return (
                    <li
                      key={row._id}
                      onClick={() => setSelectedRow(row)}
                      className="relative flex items-start gap-3 pl-0 pr-1.5 py-2 rounded-[6px] cursor-pointer hover:bg-[#f8f5ef] transition-colors"
                    >
                      {/* Tinted icon bubble sits on the timeline guide */}
                      <span
                        className="relative z-10 w-[22px] h-[22px] rounded-full flex items-center justify-center shrink-0 mt-[1px]"
                        style={{ background: bg, color, boxShadow: '0 0 0 2px #ffffff' }}
                      >
                        <Icon className="text-[11px]" />
                      </span>

                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-semibold text-[#08111f] leading-tight">
                          {label}
                        </div>
                        <div className="text-[10.5px] text-[#7a7060] mt-0.5 truncate">
                          {row.actorName
                            ? `by ${row.actorName}`
                            : row.actor
                              ? 'by deleted user'
                              : 'Anonymous (unauthenticated)'}
                          <span className="text-[#b8b0a0]"> · </span>
                          <span title={fmtAbsolute(row.createdAt)} className="tabular-nums">
                            {fmtRelative(row.createdAt)}
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-3 pt-3 border-t" style={{ borderColor: '#f0ebe0' }}>
                <button
                  type="button"
                  onClick={() => {
                    // Use the bare ROUTES path — the new-ui router is mounted
                    // at root (App.js uses path="*" → NewUIApp), so navigating
                    // to "/new-ui/admin/audit-logs" would fall through to the
                    // catch-all redirect. Sidebar + tab nav both use the bare
                    // route too. mode=audit pins the stream to audit so the
                    // targetId filter actually narrows what's shown.
                    navigate(
                      `${ROUTES.ADMIN_AUDIT_LOGS}?mode=audit&targetId=${encodeURIComponent(entityId)}`
                    );
                  }}
                  className="text-[11px] font-semibold text-[#145f5f] hover:text-[#0d4848] hover:underline inline-flex items-center gap-1"
                >
                  View full history <FiExternalLink className="text-[10.5px]" />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {selectedRow && (
        <AuditLogDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
}
